import Flex from '@react-css/flex';
import Card from 'storybookConfig/stories/cells/Card';
import Body from 'storybookConfig/stories/molecules/Body';
import Chip from 'storybookConfig/stories/molecules/Chip';
import Divider from 'storybookConfig/stories/molecules/Divider';
import Heading from 'storybookConfig/stories/molecules/Heading';
import SocialLink from 'containers/MarketplaceOnboardingPage/SocialLink';
import styled from '@emotion/styled';
import { normalizeURL } from 'utils/strings';
import type { MarketplaceFormData } from './types';
import { attributesMap } from 'components/Common/AttributesSelector';

const CardContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
`;

interface BrandInfoProps {
  marketplaceData: MarketplaceFormData | undefined;
}
const BrandInfoDescription = ({ marketplaceData }: BrandInfoProps) => {
  return (
    <CardContainer>
      <Card>
        <Flex gap="16px" column>
          <Heading variant="Headings/H2">Brand Information</Heading>
          <Divider />
          <Flex column gap="4px">
            <Body variant="Body/Body Small Bold">Social Profiles</Body>
            {marketplaceData?.websiteURL && (
              <SocialLink
                platform="website"
                url={normalizeURL(marketplaceData.websiteURL)}
                iconOnly={false}
              />
            )}
            {marketplaceData?.instagramURL && (
              <SocialLink
                platform="instagram"
                url={normalizeURL(marketplaceData.instagramURL)}
                iconOnly={false}
              />
            )}
            {marketplaceData?.facebookURL && (
              <SocialLink
                platform="facebook"
                url={normalizeURL(marketplaceData.facebookURL)}
                iconOnly={false}
              />
            )}
            {marketplaceData?.twitterURL && (
              <SocialLink
                platform="twitter"
                url={normalizeURL(marketplaceData.twitterURL)}
                iconOnly={false}
              />
            )}
            {marketplaceData?.linkedInURL && (
              <SocialLink
                platform="linkedIn"
                url={normalizeURL(marketplaceData.linkedInURL)}
                iconOnly={false}
              />
            )}
            {marketplaceData?.tikTokURL && (
              <SocialLink
                platform="tikTok"
                url={normalizeURL(marketplaceData.tikTokURL)}
                iconOnly={false}
              />
            )}
          </Flex>
          <Divider />
          <Flex column gap="4px">
            <Body variant="Body/Body Small Bold">Year Established</Body>
            <Body variant="Body/Regular">{marketplaceData?.yearEstablished}</Body>
          </Flex>
          <Flex column gap="4px">
            <Body variant="Body/Body Small Bold">Location</Body>
            <Body variant="Body/Regular">{marketplaceData?.location}</Body>
          </Flex>
          <Divider />
          <Flex column gap="4px">
            <Body variant="Body/Body Small Bold">Brand Description</Body>
            <Body variant="Body/Regular">{marketplaceData?.brandDescription}</Body>
          </Flex>
          <Flex column gap="4px">
            <Body variant="Body/Body Small Bold">Brand Attributes</Body>
            <Body variant="Body/Regular">
              <Flex gap="8px" wrap>
                {marketplaceData?.attributes?.map((attribute) => {
                  const attr = attribute ? attributesMap?.[attribute] : undefined;
                  return attr ? (
                    <Chip key={attribute} size="large">
                      {attr.label}
                    </Chip>
                  ) : null;
                })}
              </Flex>
            </Body>
          </Flex>
          <Flex column gap="4px">
            <Body variant="Body/Body Small Bold">Primary and Secondary Categories</Body>
            <Body variant="Body/Regular">
              <Flex column gap="8px">
                <Flex gap="8px" wrap>
                  {marketplaceData?.primaryCategories?.map((category) => (
                    <Chip key={category} size="large">
                      {category}
                    </Chip>
                  ))}
                </Flex>
                <Flex gap="8px" wrap>
                  {marketplaceData?.secondaryCategories?.map((category) => (
                    <Chip key={category} size="large" variant="light">
                      {category}
                    </Chip>
                  ))}
                </Flex>
              </Flex>
            </Body>
          </Flex>
        </Flex>
      </Card>
    </CardContainer>
  );
};

export default BrandInfoDescription;
